import { normalizePaymentMethodValue, PaymentMethodCode } from '../types/payment-method.enum';

/**
 * Customer-facing card payment label for printed/PDF receipts.
 * Example: `Visa •••• 1234`
 */
export function formatCardPaymentLabel(
  brand?: string | null,
  last4?: string | null
): string | null {
  const digits = last4 != null ? String(last4).trim() : '';
  if (!digits) {
    return null;
  }

  const rawBrand = brand != null ? String(brand).trim() : '';
  const labelBrand = rawBrand
    ? rawBrand.charAt(0).toUpperCase() + rawBrand.slice(1).toLowerCase()
    : 'Card';

  return `${labelBrand} •••• ${digits}`;
}

export interface ReceiptCard {
  brand?: string | null;
  last4?: string | null;
}

const CARD_PAYMENT_METHODS = new Set<string>([
  PaymentMethodCode.Card,
  PaymentMethodCode.CardPresent,
  PaymentMethodCode.Link,
  PaymentMethodCode.TapToPay,
  PaymentMethodCode.Terminal,
]);

/** Whether a payment method is paid by card (card, card present, link, tap to pay, terminal). */
export function isCardPaymentMethod(paymentMethod: string | null | undefined): boolean {
  return CARD_PAYMENT_METHODS.has(normalizePaymentMethodValue(paymentMethod));
}

/**
 * The card a receipt names for one payment: the card that payment was charged on (kept on its
 * payment row), else the order's card. A membership renews on whatever card is on file at the
 * time, while the order's card is read from its first payment, so it is only the fallback.
 * A payment made some other way (cash, a membership redemption) names no card at all.
 */
export function resolvePaymentReceiptCard(
  transaction: {
    payment_method?: string | null;
    payload?: { brand?: string; last4?: string } | null;
  },
  orderCard: ReceiptCard | null | undefined
): ReceiptCard | null {
  if (transaction.payment_method && !isCardPaymentMethod(transaction.payment_method)) {
    return null;
  }
  if (transaction.payload?.last4) {
    return { brand: transaction.payload.brand, last4: transaction.payload.last4 };
  }
  return orderCard?.last4 ? orderCard : null;
}
